import { useState, useEffect } from 'react';
import { NavLink, useLocation, Link } from 'react-router-dom';
import { UserButton } from '@clerk/clerk-react';
import {
    LayoutDashboard,
    Users,
    UserRound,
    Car,
    CalendarDays,
    ListChecks,
    FileText,
        ShieldCheck,
    ClipboardList,
    Tag,
        BarChart3,
    Megaphone,
    Menu,
    X,
    Plus,
} from 'lucide-react';

import { useCurrentUser } from '../hooks/useCurrentUser.js';
import { cn } from '../lib/cn.js';

const NAV = [
    { to: '/', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { to: '/bookings', label: 'Bookings', icon: CalendarDays },
    { to: '/chauffeurs', label: 'Chauffeurs', icon: Users },
    { to: '/vehicles', label: 'Vehicles', icon: Car },
    { to: '/customers', label: 'Customers', icon: UserRound },
       { to: '/compliance', label: 'Compliance', icon: ShieldCheck, ownerOnly: true },
    { to: '/reports', label: 'Reports', icon: ClipboardList, ownerOnly: true },
    { to: '/pricing', label: 'Pricing', icon: Tag, ownerOnly: true },
        { to: '/stats', label: 'Stats', icon: BarChart3, ownerOnly: true },
    { to: '/messages', label: 'Messages', icon: Megaphone, ownerOnly: true },
    { to: '/audit', label: 'Audit Log', icon: FileText, ownerOnly: true },
];

export function AppShell({ children }) {
    const { user, isOwner } = useCurrentUser();
    const location = useLocation();
    const [mobileNavOpen, setMobileNavOpen] = useState(false);

    // Close mobile nav when route changes
    useEffect(() => {
        setMobileNavOpen(false);
    }, [location.pathname]);

    // Lock body scroll when mobile nav is open
    useEffect(() => {
        if (mobileNavOpen) {
            document.body.style.overflow = 'hidden';
            return () => { document.body.style.overflow = ''; };
        }
    }, [mobileNavOpen]);

    const visibleNav = NAV.filter(item => !item.ownerOnly || isOwner);

    return (
        <div className="min-h-screen bg-paper">
            {/* Mobile top bar (hidden on lg+) */}
            <header className="lg:hidden fixed top-0 inset-x-0 z-30 bg-paper-card border-b border-paper-border">
                <div className="flex items-center justify-between px-4 h-14">
                    <button
                        type="button"
                        onClick={() => setMobileNavOpen(true)}
                        className="h-9 w-9 -ml-2 flex items-center justify-center rounded-md text-ink-muted hover:bg-paper-hover hover:text-ink transition-colors"
                        aria-label="Open menu"
                    >
                        <Menu className="h-5 w-5" />
                    </button>
                   <div className="flex items-center gap-2">
                        <img
                            src="/favicon-48.png"
                            alt="Lanz CTS"
                            className="h-7 w-7 rounded-md"
                        />
                        <span className="text-sm font-semibold text-ink">Lanz CTS</span>
                    </div>
                    <UserButton
                        appearance={{ elements: { userButtonAvatarBox: 'h-8 w-8' } }}
                    />
                </div>
            </header>

            {/* Mobile drawer backdrop */}
            {mobileNavOpen && (
                <div
                    className="lg:hidden fixed inset-0 z-40 bg-ink/50 backdrop-blur-sm"
                    onClick={() => setMobileNavOpen(false)}
                    aria-hidden
                />
            )}

            {/* Sidebar (desktop: fixed-left / mobile: slide-out drawer) */}
            <aside
                className={cn(
                    'fixed inset-y-0 left-0 z-40 w-64 border-r border-paper-border bg-paper-card flex flex-col',
                    'transition-transform duration-200',
                    // Desktop: always visible
                    'lg:translate-x-0 lg:w-56',
                    // Mobile: slides based on state
                    mobileNavOpen ? 'translate-x-0' : '-translate-x-full',
                )}
            >
                <div className="px-5 py-5 border-b border-paper-border-soft flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <img
                            src="/favicon-48.png"
                            alt="Lanz CTS"
                            className="h-8 w-8 rounded-md"
                        />
                        <div>
                            <div className="text-sm font-semibold text-ink leading-none">Lanz CTS</div>
                            <div className="text-2xs text-ink-subtle mt-1">Dispatch</div>
                        </div>
                    </div>
                    {/* Mobile close button inside drawer */}
                    <button
                        type="button"
                        onClick={() => setMobileNavOpen(false)}
                        className="lg:hidden h-8 w-8 flex items-center justify-center rounded-md text-ink-muted hover:bg-paper-hover hover:text-ink transition-colors"
                        aria-label="Close menu"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Primary action — sits above nav so it's always reachable from
                    any page. Green pill matches the in-form Continue/Submit
                    treatment for visual consistency across the booking flow. */}
                <div className="px-3 pt-4 pb-2">
                    <Link
                        to="/bookings/new"
                        className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 active:bg-emerald-800 transition-colors shadow-sm"
                    >
                        <Plus className="w-4 h-4" />
                        New booking
                    </Link>
                </div>

                <nav className="flex-1 px-3 pt-2 pb-4 space-y-0.5 overflow-y-auto">
                    {visibleNav.map(item => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.exact}
                            className={({ isActive }) => cn(
                                'flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                                isActive
                                    ? 'bg-ink text-paper'
                                    : 'text-ink-muted hover:bg-paper-hover hover:text-ink'
                            )}
                        >
                            <item.icon className="h-4 w-4 flex-shrink-0" />
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                {/* Desktop: user section at bottom of sidebar */}
                <div className="hidden lg:block px-3 py-4 border-t border-paper-border-soft">
                    <div className="flex items-center gap-2.5 px-2">
                        <UserButton
                            appearance={{ elements: { userButtonAvatarBox: 'h-8 w-8' } }}
                        />
                        <div className="min-w-0 flex-1">
                            <div className="text-xs font-medium text-ink truncate">{user?.full_name}</div>
                            <div className="text-2xs text-ink-subtle capitalize">{user?.role}</div>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main content */}
            <main className="lg:pl-56 pt-14 lg:pt-0">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
                    {children}
                </div>
            </main>
        </div>
    );
}
